import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { framePartyContentApi } from './contentApi';
import './FrameParty.css';

const typeLabels = { movie: 'Фильм', series: 'Сериал', anime: 'Аниме', other: 'Другое' };

const Icon = ({ name, size = 20 }) => {
  const paths = {
    play: <path d="m8 5 11 7-11 7V5Z" />,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    back: <path d="m15 18-6-6 6-6" />,
    refresh: <><path d="M20 6v5h-5" /><path d="M4 18v-5h5" /><path d="M18.5 9a7 7 0 0 0-12-2L4 11M20 13l-2.5 4a7 7 0 0 1-12-2" /></>,
    film: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 4v16M17 4v16M3 9h4M17 9h4M3 15h4M17 15h4" /></>,
    list: <><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
};

function Logo() {
  return <Link to="/" className="fp-logo" aria-label="FrameParty"><span>8%</span><b>FRAMEPARTY</b></Link>;
}

function Shell({ children }) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const submit = (event) => {
    event.preventDefault();
    navigate(`/browse${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''}`);
  };
  return <div className="fp-app">
    <header className="fp-header">
      <Logo />
      <nav aria-label="Основная навигация"><NavLink to="/" end>Главная</NavLink><NavLink to="/browse">Каталог</NavLink><NavLink to="/playlists">Плейлисты</NavLink></nav>
      <div className="fp-header-actions">
        <form className="fp-search" onSubmit={submit}><Icon name="search" size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти в каталоге" /></form>
        <a className="fp-profile" href={isAuthenticated ? 'https://profile.eight-percent.com' : `https://oauth.eight-percent.com?next=${encodeURIComponent(window.location.href)}`}><span>{user?.username?.slice(0, 1)?.toUpperCase() || 'EP'}</span><i>{user?.username || 'Войти'}</i></a>
      </div>
    </header>
    <main>{children}</main>
    <footer className="fp-footer"><Logo /><p>Смотрите вместе. Запоминайте больше.</p><span>© 2026 Eight Percent</span></footer>
  </div>;
}

function useTitles(options) {
  const query = options?.query || '';
  const type = options?.type || '';
  const [state, setState] = useState({ loading: true, error: '', items: [] });
  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await framePartyContentApi.titles({ query, type });
      setState({ loading: false, error: '', items: Array.isArray(payload?.results) ? payload.results : [] });
    } catch (error) {
      setState({ loading: false, error: error.message || 'Каталог временно недоступен.', items: [] });
    }
  }, [query, type]);
  useEffect(() => { void load(); }, [load]);
  return { ...state, reload: load };
}

function usePlaylists() {
  const [state, setState] = useState({ loading: true, error: '', items: [] });
  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await framePartyContentApi.playlists();
      setState({ loading: false, error: '', items: Array.isArray(payload?.results) ? payload.results : [] });
    } catch (error) {
      setState({ loading: false, error: error.message || 'Плейлисты временно недоступны.', items: [] });
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  return { ...state, reload: load };
}

function LoadingState() {
  return <div className="fp-state"><span className="fp-spinner" /><h2>Загружаем каталог</h2><p>FrameParty получает опубликованные карточки из Content API.</p></div>;
}

function ErrorState({ message, retry }) {
  return <div className="fp-state fp-state--error"><Icon name="refresh" size={30} /><h2>Каталог недоступен</h2><p>{message}</p><button type="button" onClick={retry}>Повторить запрос</button></div>;
}

function EmptyState({ filtered = false, title = '', text = '', icon = '' }) {
  return <div className="fp-state"><Icon name={icon || (filtered ? 'search' : 'film')} size={34} /><h2>{title || (filtered ? 'Ничего не найдено' : 'Каталог готовится')}</h2><p>{text || (filtered ? 'Измените запрос или фильтр.' : 'Здесь появятся только материалы, опубликованные через FrameParty Studio.')}</p></div>;
}

function durationLabel(seconds) {
  if (!seconds) return '';
  const total = Number(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return hours ? `${hours} ч ${minutes} мин` : `${minutes} мин`;
}

function Artwork({ item, wide = false }) {
  const source = wide ? item.backdrop || item.artwork : item.artwork;
  return <div className={`fp-artwork ${wide ? 'fp-artwork--wide' : ''}`}>{source?.url ? <img src={source.url} alt={`${wide ? 'Фон' : 'Обложка'}: ${item.title}`} /> : <span>{item.title?.slice(0, 1)?.toUpperCase() || 'FP'}</span>}<small>{source ? source.name : wide ? 'Фон не загружен' : 'Обложка не загружена'}</small></div>;
}

function MediaCard({ item }) {
  return <Link className="fp-card" to={`/title/${item.slug}`}><Artwork item={item} /><div><strong>{item.title}</strong><small>{typeLabels[item.content_type] || item.content_type}{item.year ? ` · ${item.year}` : ''}</small></div></Link>;
}

function PlaylistCard({ item }) {
  const preview = item.items?.[0] || { title: item.title };
  return <article className="fp-playlist-card"><Artwork item={preview} wide /><div><span>Редакторская подборка</span><h3>{item.title}</h3><p>{item.description || 'Подборка опубликованных материалов FrameParty.'}</p><small>{item.items?.length || 0} материалов</small></div></article>;
}

function HomePage() {
  const requestOptions = useMemo(() => ({}), []);
  const { loading, error, items, reload } = useTitles(requestOptions);
  const playlists = usePlaylists();
  if (loading) return <div className="fp-page"><LoadingState /></div>;
  if (error) return <div className="fp-page"><ErrorState message={error} retry={reload} /></div>;
  if (!items.length) return <div className="fp-page fp-page--empty"><EmptyState /></div>;
  const featured = items[0];
  return <>
    <section className="fp-hero"><Artwork item={featured} wide /><div className="fp-hero-copy"><span>Опубликовано в FrameParty</span><h1>{featured.title}</h1>{featured.original_title && <p className="fp-original">{featured.original_title}</p>}<Meta item={featured} /><p>{featured.description}</p><Link className="fp-primary" to={`/title/${featured.slug}`}>Открыть карточку</Link></div></section>
    <section className="fp-page"><div className="fp-section-head"><div><span>Общая медиатека</span><h2>Новые публикации</h2></div><Link to="/browse">Весь каталог</Link></div><div className="fp-grid">{items.map((item) => <MediaCard key={item.id} item={item} />)}</div>{playlists.items.length > 0 && <section className="fp-home-playlists"><div className="fp-section-head"><div><span>Собрано редакцией</span><h2>Плейлисты</h2></div><Link to="/playlists">Все плейлисты</Link></div><div className="fp-playlist-grid">{playlists.items.slice(0, 3).map((item) => <PlaylistCard key={item.id} item={item} />)}</div></section>}</section>
  </>;
}

function Meta({ item }) {
  const values = [typeLabels[item.content_type], item.year, item.age_rating, durationLabel(item.duration_seconds)].filter(Boolean);
  return <div className="fp-meta">{values.map((value) => <span key={value}>{value}</span>)}</div>;
}

function BrowsePage() {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const query = params.get('q') || '';
  const requestedType = params.get('type') || '';
  const [type, setType] = useState(requestedType);
  useEffect(() => setType(requestedType), [requestedType]);
  const options = useMemo(() => ({ query, type }), [query, type]);
  const { loading, error, items, reload } = useTitles(options);
  return <div className="fp-page"><div className="fp-page-head"><span>FrameParty Library</span><h1>{query ? `Результаты: «${query}»` : 'Каталог'}</h1><p>Только опубликованные материалы из серверного каталога.</p></div><div className="fp-filters">{[['','Все'],['movie','Фильмы'],['series','Сериалы'],['anime','Аниме']].map(([value,label]) => <button type="button" key={value || 'all'} className={type === value ? 'active' : ''} onClick={() => setType(value)}>{label}</button>)}</div>{loading ? <LoadingState /> : error ? <ErrorState message={error} retry={reload} /> : items.length ? <div className="fp-grid">{items.map((item) => <MediaCard key={item.id} item={item} />)}</div> : <EmptyState filtered={Boolean(query || type)} />}</div>;
}

function PlaylistsPage() {
  const { loading, error, items, reload } = usePlaylists();
  return <div className="fp-page"><div className="fp-page-head"><span>FrameParty Collections</span><h1>Плейлисты</h1><p>Только опубликованные ручные подборки из FrameParty Studio.</p></div>{loading ? <LoadingState /> : error ? <ErrorState message={error} retry={reload} /> : items.length ? <div className="fp-playlist-page-list">{items.map((playlist) => <section key={playlist.id}><div className="fp-section-head"><div><span>{playlist.items?.length || 0} материалов</span><h2>{playlist.title}</h2><p>{playlist.description}</p></div></div>{playlist.items?.length ? <div className="fp-grid">{playlist.items.map((item) => <MediaCard key={item.id} item={item} />)}</div> : <EmptyState title="Подборка пока пуста" text="Редактор опубликовал плейлист без материалов." icon="list" />}</section>)}</div> : <EmptyState title="Плейлисты готовятся" text="Здесь появятся ручные подборки, опубликованные через FrameParty Studio." icon="list" />}</div>;
}

function TitlePage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: '', item: null });
  const load = useCallback(async () => {
    setState({ loading: true, error: '', item: null });
    try { setState({ loading: false, error: '', item: await framePartyContentApi.title(slug) }); }
    catch (error) { setState({ loading: false, error: error.message || 'Карточка недоступна.', item: null }); }
  }, [slug]);
  useEffect(() => { void load(); }, [load]);
  if (state.loading) return <div className="fp-page"><LoadingState /></div>;
  if (state.error) return <div className="fp-page"><ErrorState message={state.error} retry={load} /></div>;
  const item = state.item;
  const schema = item.page_schema || {};
  return <div className="fp-title-page"><Link className="fp-back" to="/browse"><Icon name="back" />В каталог</Link><div className="fp-title-layout">{schema.hero !== false && <Artwork item={item} wide />}<article><span>{typeLabels[item.content_type] || 'Материал'}</span><h1>{item.title}</h1>{schema.facts !== false && <>{item.original_title && <p className="fp-original">{item.original_title}</p>}<Meta item={item} /><p className="fp-description">{item.description}</p><div className="fp-tags">{(item.genres || []).map((genre) => <span key={genre}>{genre}</span>)}</div></>}{schema.player && <div className="fp-playback-state"><Icon name="play" /><div><strong>Playback API ещё не подключён</strong><small>Backend не разрешает публиковать этот блок до появления защищённой media delivery.</small></div></div>}</article></div></div>;
}

export default function FramePartyApp() {
  return <Shell><Routes><Route path="/" element={<HomePage />} /><Route path="/browse" element={<BrowsePage />} /><Route path="/playlists" element={<PlaylistsPage />} /><Route path="/title/:slug" element={<TitlePage />} /><Route path="*" element={<Navigate to="/" replace />} /></Routes></Shell>;
}
